import { addMonths, format, differenceInMonths } from 'date-fns'
import React, { useState, ElementType, useEffect } from 'react'
import useAccounts from '../hooks/useAccounts'
import useCategories from '../hooks/useCategories'
import useChartData, { ChartData, XyChartPoint } from '../hooks/useChartData'
import useTags from '../hooks/useTags'

type ChartsContainerProps = {
    Layout: ElementType<any>
}

export const ChartsContainer = ({ Layout, ...props }: ChartsContainerProps) => {
    const [dateFrom, setDateFrom] = useState(addMonths(new Date(), -1))
    const [dateTo, setDateTo] = useState(addMonths(new Date(), 0))
    const [groupBy, setGroupBy] = useState('month')
    const { map: categoriesMap } = useCategories()
    const { map: tagsMap } = useTags()
    const { list: accounts } = useAccounts()
    const [focusedInput, setFocusedInput] = useState(null)

    const defaultAccounts = accounts.filter((account) => account.isExcluded !== true).map((account) => account.uid)
    const [selectedAccounts, setSelectedAccounts] = useState<string[]>(defaultAccounts)

    const handleDateFromChange = (date: Date) => {
        setDateFrom(date)
    }

    const handleDateToChange = (date: Date) => {
        setDateTo(date)
    }

    const handleGroupByChange = (groupBy: string) => {
        setGroupBy(groupBy)
    }

    const handleAccountsChange = (accounts: string[]) => {
        console.log('Accounts selected:', accounts)
        setSelectedAccounts(accounts)
    }

    const renderChildren = (chartData: ChartData) => {
        const diff = differenceInMonths(dateTo, dateFrom)
        const categoriesRangeArray: Date[] = []
        for (let i = 0; i <= diff; i += 1) {
            categoriesRangeArray.push(addMonths(dateFrom, i))
        }
        const categories = categoriesRangeArray.map((m) => format(m, 'yyyy-MM'))

        const calcAverage = (items: XyChartPoint[]) => {
            const itemsTotal = items.reduce(
                (a: number, value: XyChartPoint) => a + (value && value.y ? parseFloat(value.y) : 0),
                0
            )

            return Math.floor((100 * itemsTotal) / items.length) / 100
        }
        const cashflowAverages = {
            expense: chartData && calcAverage(chartData.expense),
            income: chartData && calcAverage(chartData.income),
        }

        return (
            <Layout
                chartData={chartData}
                onDateFromChange={handleDateFromChange}
                onDateToChange={handleDateToChange}
                onAccountsChange={handleAccountsChange}
                onGroupByChange={handleGroupByChange}
                accounts={accounts}
                selectedAccounts={selectedAccounts}
                dateFrom={dateFrom}
                dateTo={dateTo}
                groupBy={groupBy}
                categories={categories}
                categoriesMap={categoriesMap}
                tagsMap={tagsMap}
                cashflowAverages={cashflowAverages}
                focusedInput={focusedInput}
                onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
                {...props}
            />
        )
    }

    const chartData = useChartData(dateFrom, dateTo, groupBy, selectedAccounts)

    return renderChildren(chartData)
}

export default ChartsContainer
